import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../services/api";
import SkeletonLoader from "../common/SkeletonLoader";
import Button from "../common/Button";
import PaymentBadge from "../common/PaymentBadge";

export interface TodayQueueConsoleProps {
  refreshKey?: number;
  onSelectPatient?: (patient: { patient_id: string; name?: string }) => void;
  onEdit?: (patient: { patient_id: string; name?: string; gender?: string; age?: number | string }) => void;
}

interface VisitQueueItem {
  visit_id: number;
  patient_id: string;
  patient_name?: string;
  visit_date?: string;
  visit_type?: string;
  status?: string;
  appointment_time?: string | null;
  assigned_doctor_id?: number | null;
  doctor_name?: string | null;
  created_at?: string;
  gender?: string;
  age?: number | string;
  [key: string]: unknown;
}

function toIsoDate(localDate: Date = new Date()): string {
  const d = new Date(localDate);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().split("T")[0];
}


function isoToHHMM(value: unknown): string {
  const s = String(value ?? "");
  if (!s) return "";
  const t = s.includes("T") ? s.split("T")[1] : s.includes(" ") ? s.split(" ")[1] : "";
  return t ? t.slice(0, 5) : "";
}

export default function TodayQueueConsole({ refreshKey, onSelectPatient, onEdit }: TodayQueueConsoleProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<VisitQueueItem[]>([]);
  const [query, setQuery] = useState("");

  const todayISO = useMemo(() => toIsoDate(new Date()), []);
  const displayDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await apiGet(`/api/reception/today-queue?date=${todayISO}`)) as VisitQueueItem[];
      setItems(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setItems([]);
      setError((e as Error)?.message || "Failed to load today's queue");
    } finally {
      setLoading(false);
    }
  }, [todayISO]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((v) => {
      const name = (v.patient_name || "").toLowerCase();
      const pid = String(v.patient_id || "");
      return name.includes(q) || pid.includes(q);
    });
  }, [items, query]);

  const newCount = useMemo(
    () => filtered.filter((v) => (v.visit_type || "").toLowerCase() === "new").length,
    [filtered]
  );
  const oldCount = useMemo(() => Math.max(0, filtered.length - newCount), [filtered.length, newCount]);

  return (
    <section className="rc-queue">
      <header className="rc-queue-header">
        <div className="rc-queue-header-row">
          <div>
            <div className="rc-queue-title">Today's Queue</div>
            <div className="rc-queue-date">{displayDate}</div>
          </div>
          <Button variant="outline" size="sm" onClick={load} isLoading={loading}>
            Refresh
          </Button>
        </div>

        <div className="rc-queue-metrics">
          <span className="rc-queue-pill total">Total: {filtered.length}</span>
          <span className="rc-queue-pill new">New: {newCount}</span>
          <span className="rc-queue-pill old">Old: {oldCount}</span>
        </div>

        <div className="rc-queue-search">
          <span className="rc-queue-search-icon">🔍</span>
          <input
            className="rc-queue-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search patient name or ID..."
          />
        </div>
      </header>

      <div className="rc-queue-body">
        {error && <div className="rc-queue-error">{error}</div>}
        {loading ? (
           <div className="rc-queue-loading">
             <SkeletonLoader variant="list" rows={6} />
           </div>
        ) : filtered.length === 0 ? (
          <div className="mod-empty-state rc-queue-empty">No patients in queue for today.</div>
        ) : (
          filtered.map((v) => {
            const isCompleted = v.status?.toLowerCase().includes("complete");
            const statusLower = (v.status || "").toLowerCase();
            const statusClass = isCompleted
              ? "status-complete"
              : statusLower.includes("progress") || statusLower.includes("chair")
                ? "status-progress"
                : "status-open";
            const typeClass = (v.visit_type || "").toLowerCase() === "new" ? "type-new" : "type-old";
            return (
              <div
                key={v.visit_id ?? `${v.patient_id}-${v.created_at ?? ""}`}
                className={`rc-queue-item ${isCompleted ? "is-completed" : ""}`}
                onClick={() =>
                  onSelectPatient?.({
                    patient_id: String(v.patient_id),
                    name: v.patient_name,
                  })
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onSelectPatient?.({
                      patient_id: String(v.patient_id),
                      name: v.patient_name,
                    });
                  }
                }}
              >
                <div className="rc-queue-item-main">
                  <div className="rc-queue-item-title">
                    {v.patient_name || "Unknown"}
                    <PaymentBadge patientId={String(v.patient_id)} compact />
                  </div>
                  <div className="rc-queue-item-meta">
                    <span className="rc-queue-item-id">#{v.patient_id}</span>
                    <span>•</span>
                    <span className={`rc-queue-item-type ${typeClass}`}>{v.visit_type || "Walk-in"}</span>
                    {v.appointment_time ? (
                      <>
                        <span>•</span>
                        <span>🕒 {isoToHHMM(v.appointment_time) || "—"}</span>
                      </>
                    ) : null}
                    {v.doctor_name ? (
                      <>
                        <span>•</span>
                        <span>🧑‍⚕️ {v.doctor_name}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="rc-queue-item-actions">
                  <div className={`rc-queue-status ${statusClass}`}>
                    {v.status || "Open"}
                  </div>
                  {onEdit && (
                    <button
                      type="button"
                      className="rc-queue-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit({
                          patient_id: String(v.patient_id),
                          name: v.patient_name,
                          gender: v.gender,
                          age: v.age
                        });
                      }}
                    >
                      ✏️
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
